// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { ReactNode, SyntheticEvent } from 'react';
import styled from '@emotion/styled';
import Icon from 'presentations/views/components/atoms/Icon';
import { IconButton } from '@mui/material';
import Link from 'presentations/views/components/atoms/Link';
import {
  fontSize,
  mediaMax,
  spacing,
  zIndex,
} from 'presentations/views/styles';
import { black, gray01, gray03, white } from 'presentations/views/styles/color';

export interface Props {
  notificationType: 'alert' | 'info';
  title: ReactNode;
  content?: string | ReactNode | null;
  link?: string | null;
  linkTitle?: string | null;
  onClickMenu?: (e: SyntheticEvent<HTMLButtonElement>) => void;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  let icon = 'information-outline';
  switch (props.notificationType) {
    case 'alert':
      icon = 'alert-circle-outline';
      break;
    default:
      break;
  }
  return (
    <Container>
      <TitleContainer>
        <Title>
          <TitleIcon name={icon} />
          {props.title}
        </Title>
        {props.content && <Content>{props.content}</Content>}
      </TitleContainer>
      <MenuContainer>
        {props.link && (
          <StyledLink href={props.link}>
            <LinkIcon name="chevron-right" />
            {props.linkTitle || t('link title')}
          </StyledLink>
        )}
        {props.onClickMenu && (
          <IconButton onClick={props.onClickMenu}>
            <MenuIcon name="menu" />
          </IconButton>
        )}
      </MenuContainer>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 38px;
  background: ${black};
  color: ${white};
  z-index: ${zIndex(1100)};
  border-bottom: 1px dashed ${gray01};
  padding: ${spacing(4)} ${spacing(8)};
  :last-of-type {
    border: 0;
  }

  ${mediaMax.desktop} {
    justify-content: space-between;
    width: 100%;
    min-height: 48px;
  }
`;
const MenuContainer = styled.div``;
const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  margin-right: ${spacing(8)};

  ${mediaMax.tablet} {
    align-items: flex-start;
    flex-direction: column;
    margin-right: ${spacing(4)};
    font-size: ${fontSize(12)};
    line-height: ${fontSize(14)};
  }
`;
const Title = styled.div`
  display: flex;
  align-items: center;
  border-right: 1px solid ${white};
  padding-right: ${spacing(16)};
  margin-right: ${spacing(16)};
  ${mediaMax.tablet} {
    border-right: none;
    padding-right: 0;
    margin-right: 0;
    margin-bottom: ${spacing(4)};
  }
`;
const Content = styled.div`
  display: flex;
  align-items: center;
`;
const TitleIcon = styled(Icon)`
  width: 20px !important;
  height: 20px !important;
  margin-right: ${spacing(4)};

  ${mediaMax.tablet} {
    width: 14px !important;
    height: 14px !important;
  }
`;

const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  color: ${white};
  white-space: nowrap;

  &:hover {
    color: ${gray03};
  }

  ${mediaMax.tablet} {
    font-size: ${fontSize(10)};
  }
`;
const LinkIcon = styled(Icon)`
  width: 20px !important;
  height: 20px !important;

  ${mediaMax.tablet} {
    width: 14px !important;
    height: 14px !important;
  }
`;

const MenuIcon = styled(Icon)`
  width: 18px !important;
  height: 18px !important;
  color: ${white};
`;
